import React from 'react';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@frontend/shadcn/components/ui/sheet';
import { Button } from '@frontend/shadcn/components/ui/button';
import { PenToSquareIcon } from '@revfluence/fresh-icons/regular/esm';
import { StarsIcon } from '@revfluence/fresh-icons/regular/esm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { uniq } from 'lodash';
import { logger } from '@common';
import { message } from '@revfluence/fresh';
import { RefreshManageOfferItem } from '../ManageOffer/RefreshManageOfferItem';
import { BoostCards } from './BoostCards';
import { BoostCardData } from '../../utils';
import { TMember } from '../MembersWizard/types';
import { GetOfferById_offer_boosts } from '../../queries/types/GetOfferById';
import { BOOST_TYPE, BoostRewardAction } from '../../types/globalTypes';
import { useBulkActionBoostRewards } from '../../hooks/useBulkActionBoostRewards';

interface IProps {
  open: boolean;
  setIsBulkBoostsModalOpen: (open: boolean) => void;
  selectedMembers?: readonly TMember[];
  offerBoosts: GetOfferById_offer_boosts[];
  refresh?: () => void;
}

// Helper function to determine boost type and amount
const determineBoostTypeAndAmount = (boost: GetOfferById_offer_boosts) => {
  let boostType = 'cash';
  let amount = '$0';
  let discountType: string | undefined;

  switch (boost.boostType) {
    case BOOST_TYPE.CASHBACK_BOOST:
      boostType = 'cash';
      amount = `$${boost.triggerValue}`;
      break;
    case BOOST_TYPE.DISCOUNT_BOOST:
      boostType = 'discount';
      // For discount boosts, check if there's a discount offer with price rule
      if (boost.discountOffer?.promos?.[0]?.priceRuleAmount) {
        const priceRule = boost.discountOffer.promos[0];
        if (priceRule.priceRuleType === 'PERCENTAGE') {
          amount = `${priceRule.priceRuleAmount}% off`;
          discountType = 'percentage';
        } else {
          amount = `$${priceRule.priceRuleAmount} off`;
          discountType = 'fixed amount';
        }
      } else {
        amount = 'Discount Code';
        discountType = 'code';
      }
      break;
    case BOOST_TYPE.PFA_BOOST:
      boostType = 'gift';
      amount = 'Free Product';
      discountType = 'free product';
      break;
    default:
      boostType = 'cash';
      amount = `$${boost.triggerValue}`;
  }

  return { boostType, amount, discountType };
};

// Helper function to calculate total amount
const calculateTotalAmount = (
  boostType: string,
  boost: GetOfferById_offer_boosts,
  pendingBoostRewardsCount: number
): number | undefined => {
  if (boostType === 'cash' && boost.triggerValue) {
    return boost.triggerValue * pendingBoostRewardsCount;
  } else if (boostType === 'discount' && boost.discountOffer?.promos?.[0]?.priceRuleAmount) {
    const priceRule = boost.discountOffer.promos[0];
    if (priceRule.priceRuleType === 'AMOUNT') {
      return priceRule.priceRuleAmount * pendingBoostRewardsCount;
    }
  }
  return undefined;
};

// Helper function to collect pending boost rewards and affiliate IDs
const collectPendingBoostData = (
  boost: GetOfferById_offer_boosts,
  selectedMembers: readonly TMember[]
) => {
  const pendingBoostRewards: number[] = [];
  const affiliateIds: number[] = [];

  selectedMembers.forEach((member) => {
    // Check if member has boostDataForAffiliateOffer with this boost
    const memberBoostData = member.boostDataForAffiliateOffer?.find(
      (boostData) => boostData.boost.id === boost.id
    );

    if (memberBoostData) {
      // Find pending rewards for this boost
      const pendingRewards = memberBoostData.boostRewardsForAffiliate?.filter(
        (reward) => reward.status === 'PENDING'
      ) || [];

      // Add pending reward IDs to the list and collect affiliate IDs
      pendingRewards.forEach((reward) => {
        pendingBoostRewards.push(reward.id);
        affiliateIds.push(reward.affiliateId);
      });
    }
  });

  return { pendingBoostRewards, affiliateIds };
};

export const BoostApprovalDrawer: React.FC<IProps> = ({
  open,
  setIsBulkBoostsModalOpen,
  selectedMembers,
  offerBoosts,
  refresh
}) => {
  const [selectedId, setSelectedId] = React.useState<number | null>(null);
  const [step, setStep] = React.useState<BoostRewardAction | null>(null);

  const [bulkActionBoostRewards, { loading: isBulkActionLoading }] = useBulkActionBoostRewards({
    onCompleted: (data) => {
      if (data.bulkActionBoostRewards) {
        message.success('Reward updated successfully');
        onClose();
        refresh();
      }
    },
    onError: (error) => {
      logger.error('Bulk action failed:', error);
    }
  });
  // Memoized function to transform offerBoosts into BoostCardData format, filtering only pending boosts
  const boostCardsData = React.useMemo((): BoostCardData[] => {
    if (!selectedMembers || !offerBoosts) return [];

    try {
      return offerBoosts
        .map((boost) => {
          // Collect pending boost data
          const { pendingBoostRewards, affiliateIds } = collectPendingBoostData(boost, selectedMembers);
          // Get unique affiliate IDs
          const uniqueAffiliateIds = uniq(affiliateIds);
          // Only include boosts that have pending rewards
          if (pendingBoostRewards.length === 0) return null;

          // Determine boost type and amount
          const { boostType, amount, discountType } = determineBoostTypeAndAmount(boost);

          // Calculate total amount if applicable
          const totalAmount = calculateTotalAmount(boostType, boost, pendingBoostRewards.length);

          return {
            id: boost.id,
            label: boost.label,
            boostType,
            amount,
            eligibleMembersCount: uniqueAffiliateIds.length,
            pendingBoostRewards,
            totalMemberCount: selectedMembers.length,
            totalAmount,
            discountType,
          };
        })
        .filter((boostCard) => boostCard !== null) as BoostCardData[];
    } catch (error) {
      logger.error('Error transforming boost data:', error);
      return [];
    }
  }, [selectedMembers, offerBoosts]);

  const onClose = () => {
    setIsBulkBoostsModalOpen(false);
    setStep(null);
  };
  const goBack = () => {
    setStep(null);
  };
  const handleSteps = (mode: BoostRewardAction | null) => {
    setStep(mode);
    // TODO: Implement step tracking logic if needed
  };
  const handleApprove = () => {
    if (!selectedId) {
      logger.warn('No boost selected for bulk action');
      return;
    }

    const selectedBoost = boostCardsData.find((boost) => boost.id === selectedId);
    if (!selectedBoost) {
      logger.warn('Selected boost not found');
      return;
    }

    const pendingBoostRewards = selectedBoost.pendingBoostRewards;
    if (pendingBoostRewards.length === 0) {
      logger.warn('No pending boost rewards to process');
      return;
    }

    // Call the bulk action mutation
    bulkActionBoostRewards({
      variables: {
        action: step,
        boostRewardIds: pendingBoostRewards
      }
    });
  };

  // Transform the data

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="right" className="flex flex-col h-full bg-grey-0 p-0 gap-0 w-[572px] !max-w-[572px] overflow-x-hidden">
        <SheetHeader className="flex items-center gap-2 flex-row p-4">
          <SheetTitle className="!m-0 text-base font-medium">Bulk Manage Rewards</SheetTitle>
        </SheetHeader>
        <div className="border-b" />
        <div className="bg-grey-1 flex gap-2 py-2 px-4 border-b items-center">
          <Button
            onClick={goBack}
            key={2}
            className="w-8 h-8"
            variant="outline"
            size="sm"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="text-grey-6 w-4 h-4" />
          </Button>
          <div className="bg-grey-2 rounded-full w-6 h-6 flex items-center justify-center">
            <PenToSquareIcon className="h-3 w-3" />
          </div>
          <p className="text-sm font-medium text-primary m-0">{selectedMembers?.length} Members Selected</p>
        </div>
        {step === null && (
          <div className="p-4 flex flex-col gap-2">
            <p className="text-sm font-medium text-primary m-0">Reward Actions</p>
            <p className="text-sm font-normal text-grey-5 m-0">Approve or reject cash, discount, or gift product rewards earned by members.</p>
            <RefreshManageOfferItem
              title="Approve Rewards"
              icon={
                <div className="bg-pink-4 p-2 rounded-md w-6 h-6 flex items-center justify-center">
                  <StarsIcon className="text-grey-0" fontSize={18} />
                </div>
              }
              onClick={() => handleSteps(BoostRewardAction.APPROVE)}
              hoverText="Select"
            />
            <RefreshManageOfferItem
              title="Reject Rewards"
              icon={
                <div className="bg-green-4 p-2 rounded-md w-6 h-6 flex items-center justify-center">
                  <StarsIcon className="text-grey-0" fontSize={18} />
                </div>
              }
              onClick={() => handleSteps(BoostRewardAction.REJECT)}
              hoverText="Select"
            />
          </div>
        )}

        {(step === BoostRewardAction.APPROVE || step === BoostRewardAction.REJECT) && (
          <BoostCards
            selectedId={selectedId}
            setSelectedId={setSelectedId}
            boostCardsData={boostCardsData}
          />
        )}
        {(step === BoostRewardAction.APPROVE || step === BoostRewardAction.REJECT) && (
          <SheetFooter className="justify-start sm:justify-start p-4 absolute bottom-0 left-0 right-0 w-full border-t bg-grey-0 z-[99]">
            <Button
              variant="primary"
              className="w-max"
              onClick={handleApprove}
              disabled={isBulkActionLoading || !selectedId}
            >
              {isBulkActionLoading ? 'Processing...' : (step === BoostRewardAction.APPROVE ? 'Approve' : 'Reject')}
            </Button>
            <Button onClick={onClose} variant="outline" className="w-max" disabled={isBulkActionLoading}>
              Cancel
            </Button>
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
};
